/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import { useSnapshot } from 'valtio';
import { useMetaSelector } from '../../meta/reducer';
import { getRelativeTime } from '../utils';
import { posthog } from '../../lib/posthog';
import { useMapCovers } from '../use-map-covers';
import { useMapFiles } from '../use-map-files';
import { dataVersion } from '../../assistant/map-bridge';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

{
  /* <div css={css`color: ${theme.mono60}; margin: 0.2em 0;`}>
  Location: {map.suburb ?? "Unknown"}
</div> */
}

const MAPS_TO_SHOW = 6;
const DEFAULT_COVER =
  'https://birdi-public.s3.ap-southeast-2.amazonaws.com/street-background.png';

export const LatestMaps: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();
  const { maps: mapsVersion } = useSnapshot(dataVersion);

  const [maps, setMaps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  const canCreateMaps = meta.permissions.includes('workspace:map:create');

  // Fetch covers and files asynchronously
  const { getMapsWithCovers } = useMapCovers(maps);
  const { getMapsWithFiles } = useMapFiles(maps);

  // Merge both covers and files into maps
  const getMapsWithAllData = (mapsToMerge: any[]) => {
    const mapsWithCovers = getMapsWithCovers(mapsToMerge);
    return getMapsWithFiles(mapsWithCovers);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!meta.organisation?.id) {
          setMaps([]);
          setLoading(false);
          return;
        }
        const res = await mapsAPI.getOrganisationMaps(
          stroot('ma1oht'),
          meta.workspace?.id || meta.organisation.id,
          { start: 1, limit: MAPS_TO_SHOW, noCover: 'yes' },
        );
        setMaps(res.body || []);
      } catch (e) {
        setMaps([]);
      }
      setLoading(false);
    })();
    // mapsVersion bumps whenever an assistant tool renames/creates/duplicates/
    // moves a map, since those mutate the backend directly (no navigation, no
    // route remount) and this list would otherwise go stale.
  }, [meta.organisation?.id, mapsVersion]);

  const onNewMap = async () => {
    const res = (await mapsAPI.postMap(stroot('nwmap2')))?.body;
    if (res && res.id) {
      navigate(`/map/next/${res.id}`);
    }
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'latest-maps'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Latest maps</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
          `}
        >
          Maps created recently
        </div>
        {!loading && maps.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div>
              <strong>No maps created yet:</strong> Start by uploading your
              first dataset to generate maps and outputs like orthomosaics,
              DEMs, or point clouds.
            </div>
            {canCreateMaps && (
              <button
                onClick={onNewMap}
                css={css`
                  color: ${theme.deepOcean};
                  text-decoration: underline;
                  background: none;
                  border: none;
                  padding: 0;
                  font: inherit;
                  cursor: pointer;
                  margin-top: 0.5em;
                `}
              >
                + Create a map
              </button>
            )}
          </div>
        )}
        {!loading && maps.length > 0 && (
          <div>
            {getMapsWithAllData(maps).map((map) => (
              <div
                key={map.id}
                onClick={() => {
                  posthog?.capture('dashboard latest maps clicked', {
                    mapId: map.id,
                  });
                  navigate(`/map/next/${map.id}`);
                }}
                css={css`
                  display: flex;
                  align-items: center;
                  background: ${theme.mono0};
                  border-radius: 8px;
                  border: 1px solid ${theme.mono30};
                  margin-bottom: 0.5em;
                  padding: 1em;
                  gap: 1em;
                  cursor: pointer;
                  transition:
                    box-shadow 0.15s,
                    background 0.15s;
                  &:hover {
                    box-shadow: 0 3px 12px ${theme.mono30};
                  }
                `}
              >
                <img
                  src={map.mapCover || DEFAULT_COVER}
                  alt={map.name || 'Map thumbnail'}
                  css={css`
                    width: 7em;
                    height: 5em;
                    border-radius: 5px;
                    object-fit: cover;
                    background: ${theme.sand};
                  `}
                />
                {/* {console.log('map.mapCover', map.mapCover)} */}
                <div
                  css={css`
                    flex: 1;
                  `}
                >
                  <div
                    css={css`
                      font-weight: 700;
                      color: ${theme.nightSky};
                      font-size: 1.05em;
                    `}
                  >
                    {map.name || 'Untitled'}
                  </div>
                  <div
                    css={css`
                      color: ${theme.mono60};
                      margin: 0.2em 0;
                    `}
                  >
                    {(() => {
                      const gb = Number(map.totalGB);
                      if (!isNaN(gb)) {
                        if (gb < 1) {
                          return `${map.fileCount || '-'} files | ${(gb * 1024).toFixed(2)} MB`;
                        } else {
                          return `${map.fileCount || '-'} files | ${gb.toFixed(2)} GB`;
                        }
                      } else {
                        return `${map.fileCount || '-'} files | - GB`;
                      }
                    })()}
                  </div>
                  <div
                    css={css`
                      color: ${theme.mono60};
                    `}
                  >
                    {map.createdAt ? getRelativeTime(map.createdAt) : ''}
                  </div>
                </div>
                {map.status && (
                  <span
                    css={css`
                      background: ${map.status === 'Processing'
                        ? '#FFF4D5'
                        : map.status === 'Uploading'
                          ? '#E6F0FF'
                          : 'transparent'};
                      color: ${map.status === 'Processing'
                        ? '#E6A100'
                        : map.status === 'Uploading'
                          ? '#2A7BE4'
                          : theme.mono60};
                      border-radius: 6px;
                      font-size: 0.8em;
                      font-weight: 600;
                      padding: 0.2em 0.8em;
                      margin-left: 0.5em;
                    `}
                  >
                    {map.status}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
            `}
          >
            Loading...
          </div>
        )}
      </div>
      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          // disabled
          onClick={() => navigate('/new-maps/all')}
          css={css`
            width: 100%;
            color: ${theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 5px;
            padding: 0.5em;
            font-weight: 500;
            cursor: pointer;
            background: ${theme.mono0};
          `}
        >
          View all maps
        </button>
      </div>
    </div>
  );
};
